import {
  TOKO_EMBED_AUTH_MESSAGE_TYPE,
  TOKO_EMBED_CONTEXT_MESSAGE_TYPE,
  isEmbeddedInTokoHost,
  isTrustedEmbedParentOrigin,
  trustedParentOrigins,
} from "./config";
import { fetchBootstrap, type StaffWorkspaceRow } from "./graphql";
import { escapeHtml } from "./escapeHtml";
import {
  clearOAuthAccessToken,
  hydrateOAuthBearerOnWindow,
} from "./oauthSession";
import { isOAuthConfigured, redirectToTokoOAuthLogin } from "./oauthStart";

type BranchOption = {
  branchId: string;
  businessId: string;
  label: string;
};

function branchRowsFromWorkspaces(rows: StaffWorkspaceRow[]): BranchOption[] {
  const out: BranchOption[] = [];
  for (const row of rows) {
    if (row.kind !== "BRANCH" || !row.branch?._id || !row.business?._id) continue;
    const b = row.business?.name?.trim() || "Бизнес";
    const br = row.branch.name?.trim() || "Точка";
    out.push({
      branchId: row.branch._id,
      businessId: row.business._id,
      label: `${b} — ${br}`,
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, "ru"));
}

/** In-mini-app retail POS (`/pos`) with session query params for embed bootstrap. */
function buildPosEmbedUrl(businessId: string, branchId: string): string {
  const u = new URL("/pos", window.location.origin);
  u.searchParams.set("businessId", businessId);
  u.searchParams.set("branchId", branchId);
  return u.toString();
}

function goToPos(businessId: string, branchId: string): void {
  window.location.assign(buildPosEmbedUrl(businessId, branchId));
}

/** Mini-app hosted route `…/pos` — bootstraps standalone retail SPA (`pos-spa.tsx`). */
export function isRetailCashierPath(): boolean {
  const raw = (window.location.pathname || "/").replace(/\/+$/, "") || "/";
  return raw === "/pos" || raw.endsWith("/pos");
}

export function mountApp(root: HTMLElement): void {
  if (import.meta.env.PROD && trustedParentOrigins().length === 0) {
    console.warn(
      "[toko-umag] Set VITE_TRUSTED_PARENT_ORIGINS in production so only Toko shells can post embed tokens.",
    );
  }

  const hostedByToko = isEmbeddedInTokoHost();
  let authToken: string | null = hydrateOAuthBearerOnWindow() || null;
  let embedLocale: string | null = null;
  let branches: BranchOption[] = [];
  let selectedBranchId: string | null = null;
  let selectedBusinessId: string | null = null;
  let loadError: string | null = null;
  let loadingList = false;
  let oauthBusy = false;
  let oauthError: string | null = null;

  function syncSelectionFromBranchId(branchId: string | null) {
    selectedBranchId = branchId;
    const row = branchId ? branches.find((b) => b.branchId === branchId) : undefined;
    selectedBusinessId = row?.businessId ?? null;
  }

  const render = () => {
    const posReady =
      selectedBranchId &&
      selectedBusinessId &&
      /^[a-f0-9]{24}$/i.test(selectedBranchId) &&
      /^[a-f0-9]{24}$/i.test(selectedBusinessId);
    const posUrl = posReady
      ? buildPosEmbedUrl(selectedBusinessId!, selectedBranchId!)
      : null;

    root.innerHTML = `
      <div class="shell">
        <header class="u-bar">
          <span class="u-bar-brand">UMAG</span>
          <span>Касса</span>
          <span class="u-bar-ver">v ${escapeHtml(__UMAG_VERSION__)}</span>
          <span class="u-bar-dot" aria-hidden="true"></span>
        </header>
        <div class="hero">
          <h1 class="hero-title">Вход в кассу</h1>
          <p class="hero-lead">
            Выберите торговую точку — касса откроется в этом окне: продажи, возврат, смена и история продаж.
          </p>
        </div>

        <section class="panel" aria-live="polite">
          ${
            !authToken
              ? `
            <div class="panel-inner wait">
              ${hostedByToko ? `<div class="spinner" aria-hidden="true"></div>` : ""}
              <div>
                <h2 class="panel-title">${hostedByToko ? "Почти готово" : "Вход"}</h2>
                ${
                  hostedByToko
                    ? `<p class="panel-text">
                  Откройте это окно из кабинета Toko:
                  <strong>Приложения</strong> → выберите это приложение → <strong>Открыть</strong>.
                  Тогда список ваших точек подгрузится сам.
                </p>`
                    : `<p class="panel-text">
                  Войдите аккаунтом Toko — затем выберите филиал и откройте кассу Umag.
                  Мини-приложение должно быть установлено в заведении (Staff → Приложения).
                </p>`
                }
                ${
                  oauthError
                    ? `<p class="panel-text" style="color:var(--danger)">${escapeHtml(oauthError)}</p>`
                    : ""
                }
                ${
                  !hostedByToko && isOAuthConfigured()
                    ? `<div class="actions">
                  <button type="button" class="btn primary" id="btn-oauth" ${oauthBusy ? "disabled" : ""}>
                    Войти с аккаунтом Toko
                  </button>
                </div>`
                    : !hostedByToko
                      ? `<p class="panel-hint">OAuth не настроен в этой сборке. Откройте кассу из кабинета Toko.</p>`
                      : ""
                }
              </div>
            </div>
          `
              : loadingList
                ? `
            <div class="panel-inner wait">
              <div class="spinner" aria-hidden="true"></div>
              <div>
                <h2 class="panel-title">Загружаем точки</h2>
                <p class="panel-text">Секунду, подтягиваем филиалы, к которым у вас есть доступ…</p>
              </div>
            </div>
          `
                : loadError
                  ? `
            <div class="panel-inner error">
              <h2 class="panel-title">Не удалось загрузить список</h2>
              <p class="panel-text">${escapeHtml(loadError)}</p>
              <p class="panel-hint">
                Если вы администратор, проверьте адрес API в настройках сборки мини-приложения
                (<code>VITE_GRAPHQL_URL</code>). Ниже можно ввести ID филиала вручную.
              </p>
              ${manualBranchBlock()}
            </div>
          `
                  : branches.length === 0
                    ? `
            <div class="panel-inner error">
              <h2 class="panel-title">Нет доступных филиалов</h2>
              <p class="panel-text">
                Для вашего аккаунта не найдено ни одной кассовой точки. Обратитесь к владельцу бизнеса
                или выберите филиал в основном кабинете Toko и снова откройте это приложение.
              </p>
              ${manualBranchBlock()}
            </div>
          `
                    : `
            <div class="panel-inner">
              <h2 class="panel-title">Выберите точку</h2>
              <p class="panel-text">Где включить оформление Umag на этом устройстве?</p>

              <label class="field-label" for="branch-select">Филиал</label>
              <select id="branch-select" class="branch-select" autocomplete="off">
                <option value="">— выберите —</option>
                ${branches
                  .map(
                    (b) =>
                      `<option value="${escapeHtml(b.branchId)}" ${
                        b.branchId === selectedBranchId ? "selected" : ""
                      }>${escapeHtml(b.label)}</option>`,
                  )
                  .join("")}
              </select>

              <div class="actions">
                <button type="button" class="btn primary" id="btn-open" ${posUrl ? "" : "disabled"}>
                  Открыть кассу с оформлением Umag
                </button>
                <button type="button" class="btn ghost" id="btn-copy" ${posUrl ? "" : "disabled"}>
                  Скопировать ссылку для кассы
                </button>
              </div>

              <p class="footnote" id="toast" role="status"></p>
              ${
                !hostedByToko
                  ? `<div class="actions">
                <button type="button" class="btn ghost" id="btn-logout">Выйти из аккаунта Toko</button>
              </div>`
                  : ""
              }

              <details class="advanced">
                <summary>Другое: ввести ID филиала вручную</summary>
                ${manualBranchFieldsOnly()}
              </details>
            </div>
          `
          }
        </section>

        <footer class="footer">
          <p>${
            hostedByToko
              ? "Касса Umag работает внутри этого приложения — отдельный вход в POS не нужен."
              : "Можно открыть umag.toko.uz напрямую и войти аккаунтом Toko — как Hall &amp; Kitchen."
          }</p>
        </footer>
      </div>
    `;

    bind();
  };

  function manualBranchBlock(): string {
    return `
      <div class="manual-wrap">
        <label class="field-label" for="manual-branch">ID филиала (только если знаете)</label>
        <input type="text" id="manual-branch" class="text-input" inputmode="text" autocomplete="off" spellcheck="false" placeholder="24 символа" />
        <div class="actions">
          <button type="button" class="btn primary" id="btn-open-manual">Открыть кассу</button>
        </div>
        <p class="footnote" id="toast-manual" role="status"></p>
      </div>
    `;
  }

  function manualBranchFieldsOnly(): string {
    return `
      <div class="manual-nested">
        <label class="field-label" for="manual-branch-2">ID филиала</label>
        <input type="text" id="manual-branch-2" class="text-input" autocomplete="off" spellcheck="false" placeholder="24 символа" />
        <div class="actions">
          <button type="button" class="btn secondary" id="btn-open-manual-2">Открыть кассу</button>
        </div>
      </div>
    `;
  }

  function setToast(el: HTMLElement | null, text: string, ok = false) {
    if (!el) return;
    el.textContent = text;
    el.classList.toggle("toast-ok", ok);
  }

  function tryOpenFromManualBranch(
    input: HTMLInputElement | null,
    toastEl: HTMLElement | null,
  ) {
    const raw = input?.value?.trim() ?? "";
    if (!/^[a-f0-9]{24}$/i.test(raw)) {
      setToast(toastEl, "Нужен корректный ID филиала (24 символа).", false);
      return;
    }
    const row = branches.find((b) => b.branchId === raw);
    if (!row) {
      setToast(
        toastEl,
        "Филиал не найден в вашем списке. Выберите точку из списка выше или откройте приложение из кабинета Toko.",
        false,
      );
      return;
    }
    goToPos(row.businessId, row.branchId);
  }

  function bind() {
    const sel = root.querySelector<HTMLSelectElement>("#branch-select");
    if (sel) {
      sel.addEventListener("change", () => {
        syncSelectionFromBranchId(sel.value || null);
        render();
      });
    }

    root.querySelector("#btn-oauth")?.addEventListener("click", () => {
      oauthError = null;
      oauthBusy = true;
      render();
      void redirectToTokoOAuthLogin({ returnPath: "/" }).catch((e) => {
        oauthBusy = false;
        oauthError = e instanceof Error ? e.message : String(e);
        render();
      });
    });

    root.querySelector("#btn-logout")?.addEventListener("click", () => {
      clearOAuthAccessToken();
      authToken = null;
      branches = [];
      syncSelectionFromBranchId(null);
      render();
    });

    root.querySelector("#btn-open")?.addEventListener("click", () => {
      const toast = root.querySelector("#toast");
      if (!selectedBranchId || !selectedBusinessId) {
        setToast(toast as HTMLElement, "Сначала выберите филиал в списке.", false);
        return;
      }
      goToPos(selectedBusinessId, selectedBranchId);
    });

    root.querySelector("#btn-copy")?.addEventListener("click", async () => {
      const url =
        selectedBranchId && selectedBusinessId
          ? buildPosEmbedUrl(selectedBusinessId, selectedBranchId)
          : null;
      const toast = root.querySelector("#toast");
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        setToast(toast as HTMLElement, "Ссылка скопирована — отправьте её на кассу.", true);
      } catch {
        setToast(toast as HTMLElement, "Не удалось скопировать. Ссылка: " + url, false);
      }
    });

    root.querySelector("#btn-open-manual")?.addEventListener("click", () => {
      tryOpenFromManualBranch(
        root.querySelector("#manual-branch"),
        root.querySelector("#toast-manual"),
      );
    });

    root.querySelector("#btn-open-manual-2")?.addEventListener("click", () => {
      tryOpenFromManualBranch(
        root.querySelector("#manual-branch-2"),
        root.querySelector("#toast"),
      );
    });
  }

  async function loadBranches(token: string) {
    loadingList = true;
    loadError = null;
    render();
    try {
      const data = await fetchBootstrap(token, { locale: embedLocale });
      branches = branchRowsFromWorkspaces(data.getStaffWorkspaces ?? []);
      const ctxBranch = data.getContext?.branchId?.trim();
      const ctxBusiness = data.getContext?.businessId?.trim();
      if (
        ctxBranch &&
        ctxBusiness &&
        branches.some((b) => b.branchId === ctxBranch && b.businessId === ctxBusiness)
      ) {
        syncSelectionFromBranchId(ctxBranch);
      } else if (ctxBranch && branches.some((b) => b.branchId === ctxBranch)) {
        syncSelectionFromBranchId(ctxBranch);
      } else if (branches.length === 1) {
        syncSelectionFromBranchId(branches[0].branchId);
      } else {
        syncSelectionFromBranchId(null);
      }
    } catch (e) {
      loadError = e instanceof Error ? e.message : String(e);
      branches = [];
      syncSelectionFromBranchId(null);
    } finally {
      loadingList = false;
      render();
    }
  }

  window.addEventListener("message", (event: MessageEvent) => {
    if (typeof event.origin !== "string" || !isTrustedEmbedParentOrigin(event.origin)) {
      return;
    }

    const d = event.data;
    if (!d || typeof d !== "object") return;
    if (d.type === TOKO_EMBED_CONTEXT_MESSAGE_TYPE) {
      const scheme =
        d.colorScheme === "dark" || d.colorScheme === "light"
          ? d.colorScheme
          : null;
      if (scheme) {
        document.documentElement.dataset.tokoColorScheme = scheme;
      }
      if (typeof d.locale === "string" && d.locale.trim()) {
        const next = d.locale.trim();
        if (next !== embedLocale) {
          embedLocale = next;
          if (authToken) void loadBranches(authToken);
        }
      }
      return;
    }
    if (d.type !== TOKO_EMBED_AUTH_MESSAGE_TYPE) return;
    const t = d.authToken;
    if (typeof t !== "string" || !t.length) return;
    if (authToken === t) return;
    authToken = t;
    void loadBranches(t);
  });

  render();
  if (authToken) {
    void loadBranches(authToken);
  }
}
